import React, {
  Fragment,
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { Menu, Transition } from "@headlessui/react";
import { createPortal } from "react-dom";

interface DropdownProps {
  label: ReactNode;
  children: ReactNode;
  placement?: "top" | "bottom" | "left" | "right";
  className?: string;
  width?: string;
  aspectRatio?: string;
  /** Applied to the menu trigger wrapper for screen readers (icon-only triggers). */
  menuButtonAriaLabel?: string;
  // When true, the dropdown will broadcast interaction events so parent floating toolbars can stay open
  broadcastInteraction?: boolean;
}

export const DropDown: React.FC<DropdownProps> = ({
  label,
  children,
  placement = "bottom",
  className = "",
  width = "w-auto",
  /** Prefer empty for text triggers; pass `aspect-square` for icon-only toolbar buttons. */
  aspectRatio = "",
  menuButtonAriaLabel,
  broadcastInteraction = false,
}) => {
  const buttonRef = useRef<HTMLElement | null>(null);
  const [menuPosition, setMenuPosition] = useState({ top: 0, right: 0 });
  const [menuNode, setMenuNode] = useState<HTMLDivElement | null>(null);
  const menuNodeRef = useRef<HTMLDivElement | null>(null); // Track previous value to avoid loops
  const itemsRef = useRef<HTMLDivElement | null>(null);
  const [currentPlacement, setCurrentPlacement] = useState<"top" | "bottom">(
    placement === "top" ? "top" : "bottom"
  );

  // Use ref to hold the latest updatePosition to avoid stale closures
  // while keeping event listeners stable
  const updatePositionRef = useRef<() => void>(() => {});

  const updatePosition = useCallback(() => {
    if (!buttonRef.current) return;

    const rect = buttonRef.current.getBoundingClientRect();
    const viewportHeight = window.innerHeight;
    const viewportWidth = window.innerWidth;
    const menuHeight =
      itemsRef.current?.offsetHeight ?? 220; // Guess size until measured
    const spaceBelow = viewportHeight - rect.bottom;
    const spaceAbove = rect.top;
    /** Minimum inset from the viewport when clamping the fixed panel */
    const viewportPadding = 8;
    /**
     * Vertical gap between trigger and menu. Non-toolbar dropdowns use 8px.
     * Toolbar menus use a small gap so the panel sits clearly under the trigger (no overlap);
     * floating-toolbar guards (`portaledToolbarMenuOpen`, etc.) handle the pointer path.
     */
    const menuGap = broadcastInteraction ? 4 : 8;

    const shouldUseTop =
      placement === "top"
        ? spaceAbove >= menuHeight + menuGap
        : spaceBelow < menuHeight + menuGap &&
          spaceAbove >= menuHeight + menuGap;

    const newTop = shouldUseTop
      ? rect.top - menuGap - menuHeight
      : rect.bottom + menuGap;
    const newPlacement = shouldUseTop ? "top" : "bottom";
    const newRight = Math.max(viewportPadding, viewportWidth - rect.right);
    const finalTop = Math.max(viewportPadding, newTop);

    // Use functional updates with equality checks to prevent unnecessary re-renders
    setCurrentPlacement((prev) =>
      prev === newPlacement ? prev : newPlacement
    );
    setMenuPosition((prev) => {
      if (prev.top === finalTop && prev.right === newRight) {
        return prev; // Return same reference if values unchanged
      }
      return { top: finalTop, right: newRight };
    });
  }, [placement, broadcastInteraction]);

  // Keep ref updated with latest callback
  updatePositionRef.current = updatePosition;

  useEffect(() => {
    // Stable handler that uses ref
    const handlePositionUpdate = () => updatePositionRef.current();

    // Initial position update
    handlePositionUpdate();

    window.addEventListener("scroll", handlePositionUpdate, true);
    window.addEventListener("resize", handlePositionUpdate);

    return () => {
      window.removeEventListener("scroll", handlePositionUpdate, true);
      window.removeEventListener("resize", handlePositionUpdate);
    };
  }, []); // Empty deps - only run once on mount

  useLayoutEffect(() => {
    if (!menuNode) return;

    // Update position when menu node changes
    updatePositionRef.current();

    if (typeof ResizeObserver === "undefined") {
      return;
    }

    const observer = new ResizeObserver(() => {
      updatePositionRef.current();
    });
    observer.observe(menuNode);

    return () => {
      observer.disconnect();
    };
  }, [menuNode]); // Only depend on menuNode, not updatePosition

  // Track open state for broadcasting interactions via effect (not during render)
  const [isOpen, setIsOpen] = useState(false);
  const prevOpenRef = useRef(false);

  // Broadcast open/close changes via effect to avoid side effects during render
  useEffect(() => {
    if (broadcastInteraction && prevOpenRef.current !== isOpen) {
      prevOpenRef.current = isOpen;
      try {
        window.dispatchEvent(
          new CustomEvent("report-toolbar-menu-interaction", {
            detail: isOpen,
          })
        );
        (window as any).__RB_TOOLBAR_INTERACTING = isOpen;
      } catch {
        /* Silently ignore window access errors */
      }
    }
  }, [broadcastInteraction, isOpen]);

  return (
    <Menu
      as="div"
      className={`relative ${width ? "" : "inline-block"} text-left`}
    >
      {({ open }) => {
        // Update tracked open state (safe to call during render as it's deferred)
        if (open !== isOpen) {
          // Use setTimeout to defer state update to avoid render-phase setState
          setTimeout(() => setIsOpen(open), 0);
        }

        return (
          <>
            <Menu.Button as={Fragment}>
              <div
                ref={(el) => {
                  buttonRef.current = el;
                }}
                className={`${aspectRatio} inline-flex cursor-pointer`.trim()}
                aria-label={menuButtonAriaLabel}
                data-report-toolbar-trigger={
                  broadcastInteraction ? true : undefined
                }
                onMouseDown={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  if (broadcastInteraction) {
                    try {
                      window.dispatchEvent(
                        new CustomEvent("report-toolbar-menu-interaction", {
                          detail: true,
                        })
                      );
                      (window as any).__RB_TOOLBAR_INTERACTING = true;
                    } catch {
                      /* Silently ignore window access errors */
                    }
                  }
                }}
                onClick={(e) => {
                  // Prevent canvas/global click handlers from clearing selection
                  e.stopPropagation();
                }}
              >
                {label}
              </div>
            </Menu.Button>
            {typeof document !== "undefined" &&
              open &&
              createPortal(
                <Transition
                  enter="transition duration-100 ease-out"
                  enterFrom="transform scale-95 opacity-0"
                  enterTo="transform scale-100 opacity-100"
                  leave="transition duration-75 ease-out"
                  leaveFrom="transform scale-100 opacity-100"
                  leaveTo="transform scale-95 opacity-0"
                >
                  <Menu.Items
                    as="div"
                    modal={broadcastInteraction ? false : undefined}
                    ref={(node: HTMLDivElement | null) => {
                      itemsRef.current = node;
                      // Only update state if node reference actually changed
                      if (menuNodeRef.current !== node) {
                        menuNodeRef.current = node;
                        setMenuNode(node);
                      }
                    }}
                    className={`fixed z-[99999] bg-white dark:bg-slate-700 rounded-2xl shadow-lg dark:shadow-slate-900/50 focus:outline-none overflow-hidden divide-y divide-slate-100 dark:divide-slate-700 ${width} ${className}`}
                    style={{
                      top: `${menuPosition.top}px`,
                      right: `${menuPosition.right}px`,
                    }}
                    data-dropdown-placement={currentPlacement}
                    data-report-toolbar-menu="true"
                    onMouseDown={(e) => {
                      // Keep interaction inside menu from bubbling to canvas
                      // BUT don't preventDefault on buttons - it blocks their event handlers
                      const target = e.target as HTMLElement;
                      const isButton =
                        target.tagName === "BUTTON" ||
                        target.closest?.("button");
                      if (!isButton) {
                        e.preventDefault();
                      }
                      e.stopPropagation();
                    }}
                    onClick={(e) => {
                      e.stopPropagation();
                    }}
                    onMouseEnter={() => {
                      if (broadcastInteraction) {
                        try {
                          window.dispatchEvent(
                            new CustomEvent(
                              "report-toolbar-menu-interaction",
                              {
                                detail: true,
                              }
                            )
                          );
                          (window as any).__RB_TOOLBAR_INTERACTING = true;
                        } catch {
                          /* Silently ignore window access errors */
                        }
                      }
                    }}
                  >
                    <div className="p-1 cursor-pointer text-slate-700 dark:text-slate-300">
                      {children}
                    </div>
                  </Menu.Items>
                </Transition>,
                document.body
              )}
          </>
        );
      }}
    </Menu>
  );
};
